import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { convertUTCToLocal } from "../../../../utils/helper";
import { groupAndSortRecords } from "../../../../utils/outOfRangeHelpers";

/* ================= HELPERS ================= */

const safe = (v, d = "—") =>
  v === null || v === undefined || v === "" ? d : v;

/* ================= PDF GENERATION ================= */

const generateOutOfRangePDF = ({
  logo,
  title,
  generatedDate,
  totalRecords,
  completionPercentage,
  records = [],
  organizationName,
  filter = "escalated",
}) => {
  // Create PDF in landscape orientation
  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10; // 10mm margin
  const contentWidth = pageWidth - (margin * 2);
  
  
  const tableWidth = contentWidth - 2;

  let yPosition = margin;

  // Helper to add a new page if needed
  const checkPageBreak = (requiredHeight) => {
    if (yPosition + requiredHeight > pageHeight - 30) {
      pdf.addPage();
      yPosition = margin;
      // Redraw header on new page
      addHeader(pdf, logo, organizationName, title, generatedDate, margin, contentWidth);
      yPosition = 50; // After header
    }
  };

  // Add header function
  const addHeader = (doc, logoImg, orgName, reportTitle, date, leftMargin, width) => {
    let y = leftMargin;

    // Logo - 32px height (convert to mm: 32px / 3.779 = ~8.47mm)
    if (logoImg) {
      try {
        const logoHeight = 32 / 3.779; // Convert 32px to mm
        doc.addImage(logoImg, "PNG", leftMargin, y, 40, logoHeight);
        y += logoHeight + 1; // Decreased space between logo and separator
      } catch (e) {
        console.warn("Could not add logo image:", e);
      }
    }

    // Separator
    doc.setDrawColor(51, 51, 51);
    doc.setLineWidth(0.5);
    doc.line(leftMargin, y, leftMargin + 40, y);
    y += 5; // Add spacing after separator

    // Organization name
    if (orgName) {
      doc.setFontSize(12);
      doc.setTextColor(136, 136, 136);
      doc.text(orgName, leftMargin, y);
      y += 8; // Add more spacing between organization name and title
    }

    // Title row
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(34, 34, 34);
    doc.text(reportTitle, leftMargin, y);

    // Bottom border
    y += 3;
    doc.setDrawColor(224, 224, 224);
    doc.setLineWidth(0.3);
    doc.line(leftMargin, y, leftMargin + width, y);
    y += 3;

    return y;
  };

  // Add footer separator line
  const addFooterSeparator = (doc, width, height, leftMargin) => {
    const footerY = height - 15;
    const separatorY = footerY - 8;
    doc.setDrawColor(224, 224, 224);
    doc.setLineWidth(0.3);
    doc.line(leftMargin, separatorY, width - leftMargin, separatorY);
  };

  // Add footer function
  const addFooter = (doc, width, height, leftMargin, orgName, date, title, currentPage, totalPages, skipSeparator = false) => {
    const footerY = height - 15;
    
    // Top border (separator line) with more space above footer text
    // Add space between table and separator line
    if (!skipSeparator) {
      doc.setDrawColor(224, 224, 224);
      doc.setLineWidth(0.3);
      const separatorY = footerY - 8; // Space between separator and footer text
      // The table's bottom margin (20mm) should prevent overlap with this separator
      doc.line(leftMargin, separatorY, width - leftMargin, separatorY);
    }

    doc.setFontSize(8);
    doc.setTextColor(102, 102, 102);
    doc.setFont("helvetica", "normal");

    // Left: Organization name - Title
    if (orgName && title) {
      const footerTitle = `${orgName} - ${title}`;
      doc.text(footerTitle, leftMargin, footerY);
    } else if (title) {
      doc.text(title, leftMargin, footerY);
    } else if (orgName) {
      doc.text(orgName, leftMargin, footerY);
    }

    // Center: Page number
    const pageText = `Page ${currentPage} of ${totalPages}`;
    const pageTextWidth = doc.getTextWidth(pageText);
    doc.text(pageText, (width - pageTextWidth) / 2, footerY);

    // Right: Date with label (using convertUTCToLocal format)
    const dateText = `${date}`;
    const dateWidth = doc.getTextWidth(dateText);
    doc.text(dateText, width - leftMargin - dateWidth, footerY);
  };

  // Add header on first page
  yPosition = addHeader(pdf, logo, organizationName, title, generatedDate, margin, contentWidth);
  yPosition += 5; // Space after header

  // Group and sort records by patient name
  const groupedRecords = groupAndSortRecords(records);

  // Prepare table data
  const tableData = groupedRecords.map((row) => {
    const categoryText = Array.isArray(row.category)
      ? row.category.join(", ")
      : safe(row.category);

    const escalatedReviewedText =
      row.escalated_reviewed_at && row.escalated_reviewed_at !== "_ _"
        ? safe(row.escalated_reviewed_at)
        : safe(convertUTCToLocal(row.escalated_at || row.reviewed_at, navigator.language, true), "_ _");

    const notesText =
      row.recordType === "Escalated"
        ? safe(
            row.escalation_note ||
              row.escalation_notes ||
              row.note ||
              row.notes
          )
        : safe(
            row.review_note ||
              row.review_notes ||
              row.note ||
              row.notes ||
              row.interaction
          );

    return [
      safe(row.name),
      safe(row.location),
      safe(row.provider),
      safe(row.date),
      safe(row.type),
      safe(row.reading_value),
      categoryText,
      escalatedReviewedText,
      notesText,
      safe(row.recordType),
    ];
  });

  // Table columns
  const dateColumnLabel = filter === "escalated" ? "Escalated At" : "Reviewed At";
  const columns = [
    "Patient Name",
    "Location",
    "Provider",
    "Reading Date",
    "Reading Type",
    "Reading",
    "Category",
    dateColumnLabel,
    "Notes",
    "Type",
  ];

  // Calculate exact column width - each column is 10% of table width
  const columnWidth = (tableWidth / 10).toFixed(2);

  
  const footerSeparatorY = pageHeight - 15 - 8; // Where separator line is drawn
  const minSpaceBetweenTableAndSeparator = 5; // Minimum 5mm space
  const maxTableBottom = footerSeparatorY - minSpaceBetweenTableAndSeparator;
  
  autoTable(pdf, {
    head: tableData.length > 0 ? [columns] : [],
    body: tableData.length > 0 ? tableData : [[{ content: "No records found", colSpan: 10 }]],
    startY: yPosition,
    margin: { left: margin, right: margin, top: yPosition, bottom: pageHeight - maxTableBottom },
    tableWidth: tableWidth, // Fixed table width
    styles: {
      fontSize: 9,
      cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 },
      overflow: "linebreak", // Enable word wrapping
      cellWidth: "wrap", // Wrap content within cell
      lineWidth: 0.3,
      lineColor: [208, 208, 208],
      halign: "left",
      valign: "top",
      textColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [237, 231, 243],
      textColor: [43, 43, 43],
      fontStyle: "bold",
      fontSize: 9, // Increased from 7
      lineWidth: 0.3,
      lineColor: [208, 208, 208],
      cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 },
      halign: "left",
    },
    bodyStyles: {
      textColor: [0, 0, 0],
      fontSize: 9, // Increased from 7
      lineWidth: 0.3,
      lineColor: [208, 208, 208],
      cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 },
      halign: "left",
      valign: "top",
    },
    alternateRowStyles: {
      fillColor: [246, 249, 253], // #dbeaf8 in RGB
    },
    columnStyles: {
      0: { cellWidth: parseFloat(columnWidth), overflow: "linebreak", halign: "left", cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 } },
      1: { cellWidth: parseFloat(columnWidth), overflow: "linebreak", halign: "left", cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 } },
      2: { cellWidth: parseFloat(columnWidth), overflow: "linebreak", halign: "left", cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 } },
      3: { cellWidth: parseFloat(columnWidth), overflow: "linebreak", halign: "left", cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 } },
      4: { cellWidth: parseFloat(columnWidth), overflow: "linebreak", halign: "left", cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 } },
      5: { cellWidth: parseFloat(columnWidth), overflow: "linebreak", halign: "left", cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 } },
      6: { cellWidth: parseFloat(columnWidth), overflow: "linebreak", halign: "left", cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 } },
      7: { cellWidth: parseFloat(columnWidth), overflow: "linebreak", halign: "left", cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 } },
      8: { cellWidth: parseFloat(columnWidth), overflow: "linebreak", halign: "left", cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 } },
      9: { cellWidth: parseFloat(columnWidth), overflow: "linebreak", halign: "left", cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 } },
    },
    didDrawPage: (data) => {
      // Redraw header on new pages (except first page)
      if (data.pageNumber > 1) {
        addHeader(pdf, logo, organizationName, title, generatedDate, margin, contentWidth);
      }
      
      // Add footer separator line only (footer text will be added after table is complete)
      addFooterSeparator(pdf, pageWidth, pageHeight, margin);
    },
    didParseCell: (data) => {
      // Force word wrapping for all cells
      if (data.section === "body" || data.section === "head") {
        data.cell.styles.overflow = "linebreak";
        // Ensure cell width is set to the calculated column width
        if (data.column && data.column.dataKey !== undefined) {
          data.cell.styles.cellWidth = parseFloat(columnWidth);
        }
      }
    },
  });

  // Get final total pages after table is drawn
  const finalTotalPages = pdf.internal.getNumberOfPages();
  

  
  for (let pageNum = 1; pageNum <= finalTotalPages; pageNum++) {
    pdf.setPage(pageNum);
    addFooter(
      pdf,
      pageWidth,
      pageHeight,
      margin,
      organizationName,
      generatedDate,
      title,
      pageNum,
      finalTotalPages,
      true // Skip separator - already drawn
    );
  }

  return pdf;
};

export default generateOutOfRangePDF;
